import { useState, useEffect, useMemo } from 'react';
import { Plus, Upload, FileText, DollarSign, Calendar, Trash2, Check, Edit, X, Camera, Loader2, Search, ChevronUp, ChevronDown, Filter, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { expenseService } from '../services/expenseService';
import { expenseCategoryService } from '../services/expenseCategoryService';
import { driverService } from '../services/driverService';
import { documentService } from '../services/documentService';
import { toast } from 'sonner';
import { WorkiomTablePagination } from '../components/WorkiomTablePagination';
import { exportRowsToExcel } from '../utils/excelExport';

interface Expense {
  id: number;
  expenseNo?: string;
  userId?: string;
  userName?: string;
  submittedBy?: number;
  submitted_by_name?: string;
  category: string;
  amount: number;
  date: string;
  expenseDate?: string;
  description: string;
  receiptUrl?: string;
  vehicleId?: string;
  vehiclePlate?: string;
  trailerPlate?: string;
  iban?: string;
  vehicleBrand?: string;
  vehicleModel?: string;
  status: 'pending' | 'approved' | 'rejected' | 'paid';
  submittedAt?: string;
  created_at?: string;
  approvedBy?: string;
  approved_at?: string;
  rejectionReason?: string;
  notes?: string;
}

const toDateInputValue = (value?: string) => {
  if (!value) return new Date().toISOString().split('T')[0];
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  const normalized = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : new Date().toISOString().split('T')[0];
};

export function ExpenseSubmission() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [drivers, setDrivers] = useState<Array<{ id: string; name: string; userId: string; phone?: string; iban?: string; vehicleId?: string; vehiclePlate?: string; trailerPlate?: string; vehicleBrand?: string; vehicleModel?: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [previewExpense, setPreviewExpense] = useState<Expense | null>(null);
  const [saving, setSaving] = useState(false);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptOcr, setReceiptOcr] = useState<any>(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [expenseCategories, setExpenseCategories] = useState<Array<{ value: string; label: string }>>([]);
  const [formData, setFormData] = useState({
    submittedBy: '',
    category: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
  });
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'paid'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [driverSearch, setDriverSearch] = useState('');
  const [showDriverList, setShowDriverList] = useState(false);
  const [sortField, setSortField] = useState<keyof Expense | ''>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState<Partial<Record<keyof Expense, string>>>({});
  const [showFilters, setShowFilters] = useState(false);
  const [showGrouping, setShowGrouping] = useState(false);
  const [groupBy, setGroupBy] = useState<string>('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [datePreset, setDatePreset] = useState<'all' | 'last7' | 'last30' | 'thisMonth' | 'custom'>('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const columns: Array<{ key: keyof Expense; label: string }> = [
    { key: 'date', label: 'Tarih' },
    { key: 'userName', label: 'Özmal Sürücü' },
    { key: 'vehiclePlate', label: 'Çekici' },
    { key: 'trailerPlate', label: 'Dorse' },
    { key: 'iban', label: 'IBAN' },
    { key: 'expenseNo', label: 'Masraf No' },
    { key: 'category', label: 'Kategori' },
    { key: 'description', label: 'Açıklama' },
    { key: 'amount', label: 'Tutar' },
    { key: 'status', label: 'Durum' },
  ];

  useEffect(() => {
    loadExpenses();
  }, [user]);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const canViewAllExpenses = user?.role === 'admin' || user?.role === 'accountant' || user?.role === 'planner';
      
      const [response, categoryResponse, driversResponse] = await Promise.all([
        canViewAllExpenses ? expenseService.getExpenses() : expenseService.getExpenses({ driverId: user?.id?.toString() }),
        expenseCategoryService.getCategories(),
        driverService.getDrivers({ type: 'internal' }),
      ]);
      setExpenseCategories((categoryResponse.data || categoryResponse || []).map((item: any) => ({ value: item.code, label: item.label }))); 
      setDrivers((driversResponse.data || driversResponse || []).map((item: any) => ({
        id: String(item.id),
        name: item.full_name || item.name || '-',
        userId: String(item.user_id || item.userId || ''),
        phone: item.phone || item.telephone || '',
        iban: item.iban || '',
        vehicleId: item.assigned_vehicle_id ? String(item.assigned_vehicle_id) : '',
        vehiclePlate: item.assigned_vehicle_plate || item.cekici_plaka || item.vehicle_plate || '',
        trailerPlate: item.assigned_trailer_plate || item.dorse_plaka || item.trailer_plate || '',
        vehicleBrand: item.assigned_vehicle_brand || item.vehicle_brand || '',
        vehicleModel: item.assigned_vehicle_model || item.vehicle_model || '',
      })));
      
      if (response.success && response.data) {
        const mappedData: Expense[] = response.data.map((e: any) => ({
          id: e.id,
          expenseNo: e.expense_no,
          userId: e.submitted_by?.toString(),
          userName: e.submitted_by_name,
          submittedBy: e.submitted_by,
          submitted_by_name: e.submitted_by_name,
          category: e.category || '',
          amount: e.amount || 0,
          date: e.expense_date || e.date || '',
          expenseDate: e.expense_date,
          description: e.description || '',
          receiptUrl: e.receipt_path || '',
          vehicleId: e.vehicle_id ? String(e.vehicle_id) : '',
          vehiclePlate: e.vehicle_plate || '',
          trailerPlate: e.trailer_plate || '',
          iban: e.iban || '',
          vehicleBrand: e.vehicle_brand || '',
          vehicleModel: e.vehicle_model || '',
          status: mapExpenseStatus(e.status),
          submittedAt: e.created_at,
          created_at: e.created_at,
          approvedBy: e.approved_by_name,
          approved_at: e.approved_at,
          rejectionReason: e.rejection_reason,
          notes: e.notes || '',
        }));
        setExpenses(mappedData);
      }
    } catch (error) {
      console.error('Error loading expenses:', error);
      toast.error('Masraflar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const mapExpenseStatus = (status: string): Expense['status'] => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'approved';
      case 'rejected':
        return 'rejected';
      case 'paid':
        return 'paid';
      default:
        return 'pending';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if ((user?.role === 'admin' || user?.role === 'accountant' || user?.role === 'planner') && !formData.submittedBy) {
      toast.error('Lütfen bir özmal sürücü seçin');
      return;
    }

    if (!formData.category) {
      toast.error('Lütfen kategori seçin');
      return;
    }

    if (!formData.date) {
      toast.error('Lütfen tarih seçin');
      return;
    }

    if (!formData.amount || Number(formData.amount) <= 0) {
      toast.error('Lütfen geçerli bir tutar girin');
      return;
    }

    if (!formData.description?.trim()) {
      toast.error('Lütfen açıklama girin');
      return;
    }

    if (!receiptFile && !editingExpense?.receiptUrl) {
      toast.error('Lütfen fiş veya fatura dosyası yükleyin');
      return;
    }

    try {
      setSaving(true);

      let receiptPath = editingExpense?.receiptUrl || null;
      if (receiptFile) {
        const uploadData = new FormData();
        uploadData.append('file', receiptFile);
        uploadData.append('entityType', 'expense');
        uploadData.append('entityId', String(formData.submittedBy || user?.id || ''));
        uploadData.append('documentType', 'expense-receipt');
        uploadData.append('name', receiptFile.name);
        const uploadResponse = await documentService.uploadDocument(uploadData);
        receiptPath = uploadResponse?.data?.filePath || uploadResponse?.data?.path || null;
      }
      
      const expenseData = {
        submittedBy: formData.submittedBy || user?.id,
        category: formData.category,
        description: formData.description,
        amount: parseFloat(formData.amount),
        expenseDate: formData.date,
        receiptPath,
        ocrData: receiptOcr,
        suggestedCategory: receiptOcr?.suggestedCategory || null,
      };

      const response = editingExpense
        ? await expenseService.updateExpense(editingExpense.id, expenseData)
        : await expenseService.createExpense(expenseData);

      if (response.success) {
        toast.success(editingExpense ? 'Masraf güncellendi' : 'Masraf kaydedildi ve onaya gönderildi');
        await loadExpenses();
        closeForm();
      } else {
        toast.error(response.message || 'Kaydetme başarısız');
      }
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error('Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  const closeForm = () => {
    setIsFormOpen(false);
      setEditingExpense(null);
      setReceiptFile(null);
      setReceiptOcr(null);
      setDriverSearch('');
      setShowDriverList(false);
      setFormData({
        submittedBy: '',
        category: '',
        amount: '',
        date: toDateInputValue(),
      description: '',
    });
  };

  const openCreateForm = () => {
    setEditingExpense(null);
    setReceiptFile(null);
    setReceiptOcr(null);
    setDriverSearch('');
    setShowDriverList(false);
    setFormData({
      submittedBy: '',
      category: '',
      amount: '',
      date: toDateInputValue(),
      description: '',
    });
    setIsFormOpen(true);
  };

  const openEditForm = (expense: Expense) => {
    setEditingExpense(expense);
    setReceiptFile(null);
    setReceiptOcr(null);
    setDriverSearch(expense.userName || expense.submitted_by_name || '');
    setShowDriverList(false);
    setFormData({
      submittedBy: String(expense.submittedBy || expense.userId || ''),
      category: expense.category || '',
      amount: String(expense.amount || ''),
      date: toDateInputValue(expense.date || expense.expenseDate || ''),
      description: expense.description || '',
    });
    setIsFormOpen(true);
  };

  const handleDelete = async (expense: Expense) => {
    if (!confirm('Bu masraf kaydını silmek istediğinizden emin misiniz?')) return;
    try {
      const response = await expenseService.deleteExpense(expense.id);
      if (response.success) {
        toast.success('Masraf kaydı silindi');
        await loadExpenses();
      } else {
        toast.error(response.message || 'Silme başarısız');
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Silme hatası');
    }
  };

  const handleMarkPaid = async (expense: Expense) => {
    try {
      const response = await expenseService.updateExpenseStatus(expense.id, 'paid');
      if (!response.success) {
        toast.error(response.message || 'Masraf odendi olarak işaretlenemedi');
        return;
      }
      toast.success('Masraf odendi olarak işaretlendi');
      setExpenses((current) => current.map((item) => item.id === expense.id ? { ...item, status: 'paid' } : item));
    } catch (error) {
      console.error('Error marking expense paid:', error);
      toast.error('Masraf odeme adimi guncellenemedi');
    }
  };

  function getCategoryLabel(category: string) {
    return expenseCategories.find((c) => c.value === category)?.label || category;
  }

  const filteredExpenses = useMemo(() => expenses.filter((expense) => {
    if (filter !== 'all' && expense.status !== filter) return false;

    const rawDate = expense.date || expense.expenseDate || '';
    const expenseDate = rawDate ? new Date(rawDate) : null;
    const now = new Date();
    const todayEnd = new Date(now);
    todayEnd.setHours(23, 59, 59, 999);
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);

    if (!expenseDate || Number.isNaN(expenseDate.getTime())) {
      if (datePreset !== 'all') return false;
    } else {
      if (datePreset === 'last7') {
        const start = new Date(todayStart);
        start.setDate(start.getDate() - 6);
        if (expenseDate < start || expenseDate > todayEnd) return false;
      }
      if (datePreset === 'last30') {
        const start = new Date(todayStart);
        start.setDate(start.getDate() - 29);
        if (expenseDate < start || expenseDate > todayEnd) return false;
      }
      if (datePreset === 'thisMonth') {
        const start = new Date(todayStart.getFullYear(), todayStart.getMonth(), 1);
        if (expenseDate < start || expenseDate > todayEnd) return false;
      }
      if (datePreset === 'custom') {
        const start = dateRange.start ? new Date(`${dateRange.start}T00:00:00`) : null;
        const end = dateRange.end ? new Date(`${dateRange.end}T23:59:59`) : null;
        if (start && expenseDate < start) return false;
        if (end && expenseDate > end) return false;
      }
    }

    const searchMatch = !searchTerm.trim() || [
      expense.userName || expense.submitted_by_name || '',
      expense.vehiclePlate || expense.trailerPlate || '',
      expense.iban || '',
      expense.expenseNo || '',
      getCategoryLabel(expense.category),
      expense.description || '',
    ].join(' ').toLocaleLowerCase('tr-TR').includes(searchTerm.trim().toLocaleLowerCase('tr-TR'));

    const filterMatch = Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      if (key === 'category') return getCategoryLabel(expense.category).toLocaleLowerCase('tr-TR').includes(value.toLocaleLowerCase('tr-TR'));
      const cellValue = expense[key as keyof Expense];
      return String(cellValue ?? '').toLocaleLowerCase('tr-TR').includes(value.toLocaleLowerCase('tr-TR'));
    });

    return searchMatch && filterMatch;
  }), [expenses, filter, searchTerm, filters, expenseCategories, datePreset, dateRange]);

  const sortedExpenses = useMemo(() => {
    if (!sortField) return filteredExpenses;
    return [...filteredExpenses].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortOrder === 'asc' ? -1 : 1;
      if (bVal == null) return sortOrder === 'asc' ? 1 : -1;
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      const aStr = String(aVal);
      const bStr = String(bVal);
      return sortOrder === 'asc' ? aStr.localeCompare(bStr, 'tr-TR') : bStr.localeCompare(aStr, 'tr-TR');
    });
  }, [filteredExpenses, sortField, sortOrder]);

  const paginatedExpenses = useMemo(() => 
    sortedExpenses.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize)
  , [sortedExpenses, page, pageSize]);

  const groupedExpenses = useMemo(() => {
    const pageItems = sortedExpenses.slice((page - 1) * pageSize, page * pageSize);
    if (!groupBy) return { all: pageItems };
    return pageItems.reduce((groups, expense) => {
      const key = String(expense[groupBy as keyof Expense] || 'Diğer');
      if (!groups[key]) groups[key] = [];
      groups[key].push(expense);
      return groups;
    }, {} as Record<string, Expense[]>);
  }, [sortedExpenses, groupBy, page, pageSize]);

  const handleExportExcel = () => exportRowsToExcel(sortedExpenses as any[], `expense-submission-${new Date().toISOString().slice(0, 10)}.xlsx`, { sheetName: 'ExpenseSubmission' });

  const filteredDriverOptions = useMemo(() => {
    const q = driverSearch.trim().toLocaleLowerCase('tr-TR');
    if (!q) return drivers;
    return drivers.filter((driver) => (`${driver.name} ${driver.phone || ''} ${driver.vehiclePlate || ''}`).toLocaleLowerCase('tr-TR').includes(q));
  }, [drivers, driverSearch]);

  const selectedDriver = useMemo(
    () => drivers.find((driver) => driver.userId === formData.submittedBy) || null,
    [drivers, formData.submittedBy]
  );

  const handleReceiptChange = async (file: File | null) => {
    setReceiptFile(file);
    setReceiptOcr(null);
    if (!file) return;

    try {
      setOcrLoading(true);
      const response = await documentService.ocrReceipt(file);
      if (response.success && response.data) {
        setReceiptOcr(response.data);
        setFormData((current) => ({
          ...current,
          amount: response.data?.total ? String(response.data.total) : current.amount,
          category: response.data?.suggestedCategory || current.category,
          date: response.data?.date
            ? (() => {
                const raw = response.data.date.replace(/\./g, '-').replace(/\//g, '-');
                if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
                  const [day, month, year] = raw.split('-');
                  return `${year}-${month}-${day}`;
                }
                return current.date;
              })()
            : current.date,
          description: response.data?.merchant && !current.description
            ? `${response.data.merchant} fiş masrafı`
            : current.description,
        }));
      }
    } catch (error) {
      console.error('Expense receipt OCR failed:', error);
      toast.error('Evrak OCR okunamadı');
    } finally {
      setOcrLoading(false);
    }
  };

  const stats = {
    total: expenses.length,
    pending: expenses.filter(e => e.status === 'pending').length,
    approved: expenses.filter(e => e.status === 'approved').length,
    paid: expenses.filter(e => e.status === 'paid').length,
    rejected: expenses.filter(e => e.status === 'rejected').length,
    totalAmount: expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0),
    approvedAmount: expenses.filter(e => e.status === 'approved').reduce((sum, e) => sum + Number(e.amount || 0), 0),
    paidAmount: expenses.filter(e => e.status === 'paid').reduce((sum, e) => sum + Number(e.amount || 0), 0),
  };

  const getStatusBadge = (status: Expense['status']) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Beklemede</span>;
      case 'approved':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Onaylandı</span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Reddedildi</span>;
      case 'paid':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Ödendi</span>;
    }
  };

  const renderCell = (expense: Expense, key: string) => {
    switch (key) {
      case 'date':
        return <span className="text-sm text-gray-900">{new Date(expense.date || expense.expenseDate || '').toLocaleDateString('tr-TR')}</span>;
      case 'userName':
        return <span className="text-sm font-medium text-gray-900">{expense.userName || expense.submitted_by_name || '-'}</span>;
      case 'vehiclePlate':
        return <span className="text-sm text-gray-900">{expense.vehiclePlate || '-'}</span>;
      case 'trailerPlate':
        return <span className="text-sm text-gray-900">{expense.trailerPlate || '-'}</span>;
      case 'iban':
        return <span className="text-sm font-mono text-gray-900">{expense.iban || '-'}</span>;
      case 'expenseNo':
        return <span className="text-sm text-gray-500">{expense.expenseNo || '-'}</span>;
      case 'category':
        return <span className="text-sm text-gray-900">{getCategoryLabel(expense.category)}</span>;
      case 'description':
        return <p className="text-sm text-gray-900">{expense.description}</p>;
      case 'amount':
        return <span className="text-sm font-semibold text-gray-900">₺{Number(expense.amount || 0).toFixed(2)}</span>;
      case 'status':
        return getStatusBadge(expense.status);
      default:
        return null;
    }
  };

  const toggleSelect = (id: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedExpenses.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(paginatedExpenses.map(e => e.id)));
  };

  const handleSort = (field: keyof Expense) => {
    if (sortField === field) {
      setSortOrder((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortField(field);
    setSortOrder('asc');
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setDatePreset('all');
    setDateRange({ start: '', end: '' });
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`${selectedIds.size} masraf silinsin mi?`)) return;
    
    try {
      setLoading(true);
      for (const id of selectedIds) {
        await expenseService.deleteExpense(id);
      }
      toast.success(`${selectedIds.size} masraf silindi`);
      setSelectedIds(new Set());
      loadExpenses();
    } catch (err) {
      toast.error('Silme hatası');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Masraf Girişi</h1>
            <p className="text-sm text-gray-600 mt-1">Masraflarınızı kaydedin ve onaya gönderin</p>
          </div>
          <button
            onClick={openCreateForm}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Masraf Ekle
          </button>
        </div>
      </div>

      {/* Stage Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <button type="button" onClick={() => setFilter('all')} className={`rounded-2xl border p-4 text-left transition-colors ${filter === 'all' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Tümü</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </button>

        <button type="button" onClick={() => setFilter('pending')} className={`rounded-2xl border p-4 text-left transition-colors ${filter === 'pending' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Bekleyen</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
            </div>
          </div>
        </button>

        <button type="button" onClick={() => setFilter('approved')} className={`rounded-2xl border p-4 text-left transition-colors ${filter === 'approved' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Onaylanan</p>
              <p className="text-2xl font-bold text-gray-900">{stats.approved}</p>
            </div>
          </div>
        </button>

        <button type="button" onClick={() => setFilter('paid')} className={`rounded-2xl border p-4 text-left transition-colors ${filter === 'paid' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Ödenen</p>
              <p className="text-2xl font-bold text-gray-900">{stats.paid}</p>
            </div>
          </div>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-600">Toplam Masraf Tutarı</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">₺{stats.totalAmount.toFixed(2)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-600">Onaylanan Tutar</p>
          <p className="mt-1 text-2xl font-bold text-emerald-700">₺{stats.approvedAmount.toFixed(2)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-600">Ödenen Tutar</p>
          <p className="mt-1 text-2xl font-bold text-purple-700">₺{stats.paidAmount.toFixed(2)}</p>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between mb-4">
          <span className="text-blue-700 font-medium">{selectedIds.size} kayıt seçili</span>
          <div className="flex items-center gap-2">
            <button onClick={bulkDelete} className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700">Sil</button>
            <button onClick={() => setSelectedIds(new Set())} className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded">İptal</button>
          </div>
        </div>
      )}

      <div className="workiom-toolbar !justify-start !pt-0">
        <div className="flex flex-wrap items-center gap-3 w-full">
          <div className="workiom-search max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Sürücü, plaka, masraf no veya açıklama ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="outline-none"
            />
          </div>
          <button
            onClick={() => setShowFilters((current) => !current)}
            className={`workiom-outline-button ${showFilters ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}
          >
            <Filter className="w-4 h-4" /> Filtrele
          </button>
          <select value={datePreset} onChange={(e) => setDatePreset(e.target.value as typeof datePreset)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700">
            <option value="all">Tüm Tarihler</option>
            <option value="last7">Son 7 Gün</option>
            <option value="last30">Son 30 Gün</option>
            <option value="thisMonth">Bu Ay</option>
            <option value="custom">Özel Tarih</option>
          </select>
          {datePreset === 'custom' && (
            <>
              <input type="date" value={dateRange.start} onChange={(e) => setDateRange((current) => ({ ...current, start: e.target.value }))} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700" />
              <input type="date" value={dateRange.end} onChange={(e) => setDateRange((current) => ({ ...current, end: e.target.value }))} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700" />
            </>
          )}
          <button type="button" onClick={handleExportExcel} className="workiom-outline-button">Excele Aktar</button>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="workiom-outline-button">
              <XCircle className="w-4 h-4" /> Filtreleri Temizle
            </button>
          )}
          <div className="flex items-center gap-2">
            <button onClick={() => setShowGrouping((current) => !current)} className={`workiom-outline-button ${showGrouping ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}>
              <Filter className="w-4 h-4" /> Gruplandır
            </button>
            {showGrouping && (
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg">
                <option value="">Gruplama Yok</option>
                <option value="status">Durum</option>
                <option value="category">Kategori</option>
                <option value="userName">Özmal Sürücü</option>
                <option value="vehiclePlate">Çekici</option>
                <option value="trailerPlate">Dorse</option>
                <option value="iban">IBAN</option>
              </select>
            )}
          </div>
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">
            {filter === 'all' ? 'Tum Masraflar' : filter === 'pending' ? 'Bekleyen Masraflar' : filter === 'approved' ? 'Onaylanan Masraflar' : 'Odenen Masraflar'}
          </p>
          <p className="text-xs text-gray-500 mt-1">Masraf akisini finansal islemler mantiginda asama bazli takip edin.</p>
        </div>
        {stats.rejected > 0 && <div className="text-xs font-medium text-red-600">Reddedilen: {stats.rejected}</div>}
      </div>

      {/* Expenses List */}
      <div className="workiom-grid-wrap border border-gray-200 bg-white">
        <table className="workiom-grid-table text-sm">
            <thead>
              <tr>
                <th className="workiom-grid-index text-left">
                  <input 
                    type="checkbox" 
                    checked={selectedIds.size === paginatedExpenses.length && paginatedExpenses.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                </th>
                {columns.map((column) => (
                  <th 
                    key={column.key}
                    onClick={() => handleSort(column.key)}
                    className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  >
                    <div className="flex items-center gap-2">
                      {column.label}
                      {sortField === column.key && (
                        sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                ))}
                <th className="workiom-grid-sticky-right text-left">
                  İşlemler
                </th>
              </tr>
              {showFilters && (
                <tr>
                  <th className="workiom-grid-index"></th>
                  {columns.map((column) => (
                    <th key={`filter-${column.key}`} className="px-2 py-1.5">
                      <input 
                        value={filters[column.key as keyof Expense] || ''} 
                        onChange={(e) => setFilters({ ...filters, [column.key]: e.target.value })} 
                        placeholder={`${column.label} filtre`} 
                        className="w-full rounded border border-gray-300 px-2 py-1 text-xs" 
                      />
                    </th>
                  ))}
                  <th className="workiom-grid-sticky-right"></th>
                </tr>
              )}
            </thead>
            <tbody>
              {Object.entries(groupedExpenses).flatMap(([groupKey, groupItems]) => {
                const rows: any[] = [];
                if (groupBy) {
                  rows.push(
                    <tr key={`group-${groupKey}`} className="bg-gray-100">
                      <td colSpan={columns.length + 2} className="px-3 py-2">
                        <button type="button" onClick={() => setCollapsedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))} className="flex items-center gap-2 text-sm font-medium text-gray-900">
                          {collapsedGroups[groupKey] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                          <span>{groupKey}</span>
                          <span className="text-gray-500">({groupItems.length})</span>
                        </button>
                      </td>
                    </tr>
                  );
                }
                
                if (!collapsedGroups[groupKey]) {
                  rows.push(...groupItems.map((expense) => (
                    <tr key={expense.id} className="hover:bg-gray-50">
                      <td className="workiom-grid-index align-middle">
                        <input 
                          type="checkbox" 
                          checked={selectedIds.has(expense.id)}
                          onChange={() => toggleSelect(expense.id)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                      </td>
                      {columns.map((column) => (
                        <td key={column.key} className="px-4 py-2.5 align-middle">
                          {renderCell(expense, column.key)}
                        </td>
                      ))}
                      <td className="workiom-grid-sticky-right px-4 py-2.5 align-middle">
                        <div className="flex items-center justify-end gap-2">
                          {expense.status !== 'paid' && (
                            <button type="button" onClick={() => openEditForm(expense)} className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50" title="Düzenle">
                              <Edit className="w-4 h-4" />
                            </button>
                          )}
                          {expense.status === 'approved' && (user?.role === 'admin' || user?.role === 'accountant') && (
                            <button type="button" onClick={() => void handleMarkPaid(expense)} className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50" title="Ödendi İşaretle">
                              <DollarSign className="w-4 h-4" />
                            </button>
                          )}
                          {expense.status === 'pending' && (
                            <button type="button" onClick={() => handleDelete(expense)} className="p-1.5 rounded-lg text-red-600 hover:bg-red-50" title="Sil">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )));
                }
                
                return rows;
              })}
            </tbody>
          </table>
        
        {paginatedExpenses.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {searchTerm ? 'Arama sonucu bulunamadı' : 'Henüz masraf kaydı yok'}
          </div>
        )}
      </div>
      <div className="mb-6 flex items-center justify-end gap-3">
        <WorkiomTablePagination page={page} pageSize={pageSize} totalRows={sortedExpenses.length} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
      </div>
      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">{editingExpense ? 'Masraf Düzenle' : 'Yeni Masraf Ekle'}</h2>
              <button
                onClick={closeForm}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} noValidate className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {(user?.role === 'admin' || user?.role === 'accountant' || user?.role === 'planner') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Özmal Sürücü *
                    </label>
                    <div className="relative">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          value={formData.submittedBy ? (drivers.find((driver) => driver.userId === formData.submittedBy)?.name || driverSearch) : driverSearch}
                          onChange={(e) => {
                            setDriverSearch(e.target.value);
                            setFormData({ ...formData, submittedBy: '' });
                            setShowDriverList(true);
                          }}
                          onFocus={() => setShowDriverList(true)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Sürücü veya plaka ara"
                          required={!formData.submittedBy}
                        />
                      </div>
                      {showDriverList && (
                        <div className="absolute z-20 mt-2 w-full max-h-72 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                          {filteredDriverOptions.map((driver) => (
                            <button key={driver.id} type="button" onClick={() => { setFormData({ ...formData, submittedBy: driver.userId }); setDriverSearch(driver.name); setShowDriverList(false); }} className="block w-full px-4 py-2 text-left hover:bg-gray-50">
                              <div className="font-medium text-gray-900">{driver.name}</div>
                              <div className="text-xs text-gray-500">{driver.vehiclePlate || '-'}{driver.trailerPlate ? ` / ${driver.trailerPlate}` : ''}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedDriver && (
                  <div className="col-span-2 grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 md:grid-cols-3">
                    <div>
                      <p className="text-xs font-medium text-slate-500">Aktif Çekici</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{selectedDriver.vehiclePlate || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-500">Aktif Dorse</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{selectedDriver.trailerPlate || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-500">IBAN</p>
                      <p className="mt-1 break-all text-sm font-semibold text-slate-900">{selectedDriver.iban || '-'}</p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kategori *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Seçin...</option>
                    {expenseCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tarih *
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tutar (₺) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Açıklama *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Masraf detaylarını girin..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fiş / Fatura Dosyası *
                </label>
                {editingExpense?.receiptUrl && (
                  <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span className="text-xs font-medium text-gray-600">Mevcut Evrak</span>
                      <button
                        type="button"
                        onClick={() => setPreviewExpense(editingExpense)}
                        className="text-xs font-medium text-blue-600 hover:text-blue-700"
                      >
                        Görseli Aç
                      </button>
                    </div>
                    {editingExpense.receiptUrl.match(/\.pdf($|\?)/i) ? (
                      <div className="rounded border border-gray-200 bg-white px-3 py-6 text-center text-sm text-gray-500">
                        PDF evrak yüklü
                      </div>
                    ) : (
                      <img
                        src={editingExpense.receiptUrl.startsWith('http') ? editingExpense.receiptUrl : `${(import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '')}${editingExpense.receiptUrl}`}
                        alt="Masraf evrakı"
                        className="max-h-48 w-full rounded border border-gray-200 object-contain bg-white"
                      />
                    )}
                  </div>
                )}
                <label className="flex items-center justify-center gap-2 w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors">
                  <Upload className="w-5 h-5 text-gray-500" />
                  <span className="text-sm text-gray-600">
                    {receiptFile ? receiptFile.name : editingExpense?.receiptUrl ? 'Yeni dosya seçerek mevcut evrağı değiştirin' : 'Dosya seçin (PDF veya görsel)'}
                  </span>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    onChange={(e) => {
                      void handleReceiptChange(e.target.files?.[0] || null);
                    }}
                  />
                </label>
                {ocrLoading && <p className="mt-2 text-xs text-blue-600">OCR okunuyor...</p>}
                {!ocrLoading && receiptOcr && (
                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                    OCR bulundu: {receiptOcr.merchant || '-'}{receiptOcr.total ? ` • ₺${Number(receiptOcr.total).toLocaleString('tr-TR')}` : ''}{receiptOcr.taxNumber ? ` • VKN: ${receiptOcr.taxNumber}` : ''}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeForm}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={saving}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Kaydediliyor...
                    </>
                  ) : (
                    'Kaydet ve Onaya Gönder'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {previewExpense && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Masraf Evrakı</h2>
                <p className="text-sm text-gray-500 mt-1">{previewExpense.expenseNo || '-'}</p>
              </div>
              <button onClick={() => setPreviewExpense(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {previewExpense.receiptUrl ? (
                <div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                  {previewExpense.receiptUrl.match(/\.pdf($|\?)/i) ? (
                    <iframe
                      src={previewExpense.receiptUrl.startsWith('http') ? previewExpense.receiptUrl : `${(import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '')}${previewExpense.receiptUrl}`}
                      className="w-full h-[70vh]"
                      title="Masraf Evrakı"
                    />
                  ) : (
                    <img
                      src={previewExpense.receiptUrl.startsWith('http') ? previewExpense.receiptUrl : `${(import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '')}${previewExpense.receiptUrl}`}
                      alt="Masraf Evrakı"
                      className="w-full max-h-[70vh] object-contain bg-white"
                    />
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-500">Yüklü evrak bulunamadı.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
